import 'react-phone-input-2/lib/style.css'
import './styles.scss'

import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness'
import Button from '@mui/material/Button'
import Analytics, {
  EventCategory,
  EventProperties,
  Events
} from 'analytics/Analytics'
import {
  OnboardingIcon,
  tfmBg,
  groupcalBg,
  groupcalBgDark,
  logo24me,
  tfmBgDark
} from 'assets/img'

import { LoadingSpinner } from 'components/containers/styled'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useConnection } from 'components/providers/ConnectionProvider'
import { FirebaseError } from 'firebase/app'
import useTabActive from 'hooks/useTabActive'
import i18n from 'i18n'
import { startsWith } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-phone-input-2'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import PinInput from 'react-pin-input'
import { cacheAndLogin, signUp } from 'services/api/auth'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { CalendarRange } from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import { APP_COLOR } from 'utils/colors'
import {
  generateRecaptcha,
  verifyPhoneNumber,
  verifyPinCode
} from 'utils/firebase'
import { userLang } from 'utils/lang'
import ReCAPTCHA from 'react-google-recaptcha'
import { BlockContainer, MiscText, TextButton } from './styled'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import TwentyfourLoginInputs from './TwentyfourLoginInputs'
import { isValidEmail } from 'components/lists/ParticipantList'
import { GENERIC_ERROR } from 'utils/strings'
import { AxiosError } from 'axios'
import { forgotPassword, updateAccountOnServer } from 'services/api/account'
import PhoneTypePicker from './PhoneTypePicker'
import { CountdownButton } from './CountdownButton'
import { requestSMSFromCheckmobi, validatePin } from 'services/api/checkmobi'
import { CheckmobiValidationRequest } from 'types/api/CheckmobiValidationRequest'
import { CodeProvider, useAuth } from 'components/providers/AuthProvider'
import { CheckmobiCheckPinRequest } from 'types/api/CheckmobiCheckPinRequest'
import { DocPhoneNumberType } from 'utils/auth'
import dayjs from 'dayjs'
import { isTestEnv, sendEmailToUs } from 'utils/env'
import DebugPanel from './DebugPanel'
import { isCypress } from 'utils/cypress'
import { Account } from 'services/db/Queries/AccountQueries'
import Database from 'services/db/Database'
import { AccountDoc } from 'types/api/changes'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import { isUserOutOfTierBasedOnLandline } from 'utils/tier'
import { ChatbaseButton } from 'components/calendar/CalendarView'
import { t } from 'i18next'
import { ShieldCheck } from 'assets/icons'
import SelectItemWithSearchPopup from 'components/popups/SelectItemWithSearchPopup'
import IconButton from 'components/buttons/IconButton'
import {
  APP_THEME_MODES,
  AppThemeMode,
  useAppSettings
} from 'components/providers/AppSettingsProvider'

export enum PhoneType {
  Landline = 'Landline',
  Mobile = 'Mobile'
}

export enum UISTate {
  REGULAR,
  FORGOT_PASSWORD,
  DONE_FORGOT_PASSWORD
}

export interface CountryData {
  name: string
  dialCode: string
  countryCode: string
  format: string
}

interface LoginPageProps {
  onAuth: (b: boolean) => void
  isAuth: boolean
}

const phoneNums = process.env.REACT_APP_TEST_PHONE_NUMS?.split(',') || []

export default function LoginPage(props: LoginPageProps) {
  const theme = useTheme()
  const appSettings = useAppSettings()
  const [showThemePopup, setShowThemePopup] = useState(false)
  const [imgLoaded, onImgLoaded] = useState(false)
  const [generateCaptcha, onGenerateCaptcha] = useState(false)
  const [showCode, onShowCode] = useState(false)
  const [phone, setPhone] = useState<string>('')
  const [countryCode, setCountryCode] = useState<string>('')

  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const auth = useAuth()

  useEffect(() => {
    Analytics.getInstance().sendEvent(Events.FIRST_SCREEN, {})
    Analytics.getInstance().sendEvent(Events.PAGE_LOAD_ENTER_PHONE, {})

    if (props.isAuth) {
      recaptchaRef.current?.reset() // Reset the reCAPTCHA instance
      window.recaptchaVerifier = undefined // Clear the recaptchaVerifier
    }
  }, [props.isAuth])

  // check that phone is equal to one of test phones
  useEffect(() => {
    if (
      phoneNums.some((num) => {
        return phone.includes(num)
      })
    ) {
      auth.onCodeProvider(CodeProvider.Firebase)
    }
  }, [phone])

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const onRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token)
  }

  const themeOptions = [
    { id: APP_THEME_MODES.SYSTEM, title: i18n.t('system') },
    { id: APP_THEME_MODES.LIGHT, title: i18n.t('light') },
    { id: APP_THEME_MODES.DARK, title: i18n.t('dark') }
  ]

  const ThemeIcon =
    appSettings.themeMode === APP_THEME_MODES.DARK
      ? DarkModeIcon
      : appSettings.themeMode === APP_THEME_MODES.LIGHT
        ? LightModeIcon
        : SettingsBrightnessIcon

  const executeRecaptcha = async () => {
    if (recaptchaRef.current) {
      const token = await recaptchaRef.current.executeAsync()
      setRecaptchaToken(token)

      return token
    }
    return null
  }

  return (
    <Box
      width={'100vw'}
      height={'100vh'}
      sx={{ backgroundColor: 'var(--color-surface-card)' }}
      onClick={(e) => {
        if (!showCode) return

        if (
          e.target instanceof Element &&
          e.target.closest('.verify-pin-container')
        ) {
          return
        }

        const container = document.querySelector('.verify-pin-container')
        if (container) {
          const inputs = Array.from(
            container.querySelectorAll<HTMLInputElement>('input')
          )
          const firstEmpty = inputs.find((input) => !input.value)
          ;(firstEmpty ?? inputs[inputs.length - 1])?.focus()
        } else {
          pinInput?.focus()
        }
      }}
    >
      {isGroupcalApp() && (
        <ReCAPTCHA
          ref={recaptchaRef}
          onChange={onRecaptchaChange}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
          size="invisible"
        />
      )}
      <Box position={'absolute'} top={20} right={20} zIndex={2}>
        <SelectItemWithSearchPopup
          open={showThemePopup}
          setOpen={setShowThemePopup}
          data={themeOptions}
          selectedItemId={appSettings.themeMode}
          selectItem={(item) => {
            const mode = item.id as AppThemeMode
            if (
              mode === APP_THEME_MODES.SYSTEM ||
              mode === APP_THEME_MODES.LIGHT ||
              mode === APP_THEME_MODES.DARK
            ) {
              appSettings.setThemeMode(mode)
            }
            setShowThemePopup(false)
          }}
        >
          <IconButton
            tooltip={i18n.t('theme')}
            id="login-theme-switcher-button"
            size="md"
            onClick={() => {
              setShowThemePopup(true)
            }}
          >
            <ThemeIcon htmlColor={theme.palette.text.primary} />
          </IconButton>
        </SelectItemWithSearchPopup>
      </Box>
      <Box position={'absolute'} right={20} bottom={85}>
        <ChatbaseButton />
      </Box>

      {!imgLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'var(--color-surface-card)',
            zIndex: 9999
          }}
        >
          <LoadingSpinner />
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: imgLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        {isTestEnv() ? <DebugPanel /> : <></>}

        {/* Container sized to the image */}
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            width: 'fit-content',
            height: 'fit-content',
            maxWidth: '100%',
            maxHeight: '100%',
            '@media (max-width: 990px)': {
              backgroundColor: 'var(--color-surface-card)'
            }
          }}
        >
          {/* Background Image - establishes container size */}
          <img
            src={
              is24meApp()
                ? theme.palette.mode === 'dark'
                  ? tfmBgDark
                  : tfmBg
                : theme.palette.mode === 'dark'
                  ? groupcalBgDark
                  : groupcalBg
            }
            alt="Background"
            onLoad={() => onImgLoaded(true)}
            style={{
              display: 'block',
              width: '100%',
              minWidth: '920px',
              height: 'auto',
              maxHeight: '90vh'
            }}
          />

          {/* White overlay for small screens */}
          <Box
            sx={{
              display: 'none',
              '@media (max-width: 990px)': {
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--color-surface-card)',
                zIndex: 0
              },
              '@media (max-height: 700px)': {
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--color-surface-card)',
                zIndex: 0
              }
            }}
          />

          {/* Content Grid - positioned absolutely inside */}
          <Grid
            container
            direction={'row'}
            display={'flex'}
            wrap={'nowrap'}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              '@media (min-width: 1500px)': {
                paddingTop: '9rem'
              },
              '@media (min-width: 1200px) and (max-width: 1499px)': {
                paddingTop: '7rem'
              },
              '@media (min-width: 990px) and (max-width: 1199px), (max-height: 900px)':
                {
                  paddingTop: '5rem'
                },

              width: '100%',
              height: '100%',
              zIndex: 1
            }}
          >
            <Box
              sx={{
                flex: { xs: 'auto', md: 1 },
                display: 'flex',
                alignItems: 'top',
                justifyContent: 'center',
                paddingLeft: { xs: 2, md: 4 },
                width: { xs: '100%', md: 'auto' },
                '@media (max-width: 990px)': {
                  width: '100%',
                  paddingLeft: 0,
                  paddingRight: 0
                }
              }}
            >
              <Box
                display={'flex'}
                flexDirection={'column'}
                width={'70%'}
                height={'fit-content'}
                gap={8}
                sx={{
                  backgroundColor: 'var(--color-surface-card)',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  borderRadius: '40px',
                  p: '1.5rem',
                  '@media (min-width: 1200px)': {
                    p: '2rem'
                  }
                }}
              >
                {showCode ? (
                  <CodeInputBlock
                    countryCode={countryCode}
                    onGenerateCaptcha={onGenerateCaptcha}
                    phone={phone}
                    showCode={onShowCode}
                    executeRecaptcha={executeRecaptcha}
                    {...props}
                  />
                ) : (
                  <PhoneInputBlock
                    countryCode={countryCode}
                    setCountryCode={setCountryCode}
                    onGenerateCaptcha={onGenerateCaptcha}
                    onShowCode={onShowCode}
                    phone={phone}
                    setPhone={setPhone}
                    executeRecaptcha={executeRecaptcha}
                    {...props}
                  />
                )}
              </Box>
            </Box>

            {/* Right Side Image and Text */}
            <Box
              sx={{
                flex: 1,
                display: { xs: 'none', md: 'flex' },
                paddingTop: { xs: 4, md: 8 },
                paddingRight: { xs: 2, md: 6 },
                flexDirection: 'column',
                gap: 4,
                '@media (max-width: 990px)': {
                  display: 'none'
                },
                '@media (max-height: 700px)': {
                  display: 'none'
                }
              }}
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                width={'fit-content'}
                alignItems={'center'}
                gap={2}
              >
                {is24meApp() ? (
                  <img
                    style={{ width: '200px' }}
                    src={logo24me}
                    alt="Background1"
                  />
                ) : (
                  <OnboardingIcon
                    width={40}
                    height={40}
                    style={{
                      width: 'clamp(28px, 3vw, 40px)',
                      height: 'clamp(28px, 3vw, 40px)'
                    }}
                  />
                )}
                {isGroupcalApp() && (
                  <Typography
                    color={theme.palette.text.primary}
                    sx={{
                      fontWeight: 300,
                      '@media (min-width: 1500px)': {
                        fontSize: '40px',
                        lineHeight: '38px'
                      },
                      '@media (min-width: 1200px) and (max-width: 1499px)': {
                        fontSize: '30px',
                        lineHeight: '34px'
                      },
                      '@media (min-width: 990px) and (max-width: 1199px), (max-height: 900px)':
                        {
                          fontSize: '26px',
                          lineHeight: '30px'
                        }
                    }}
                  >
                    {is24meApp() ? t('appName24me') : t('appName')}
                  </Typography>
                )}
              </Box>

              <Box gap={2} display={'flex'} flexDirection={'column'}>
                <TitleComponent
                  textAlign={i18n.dir() === 'ltr' ? 'left' : 'right'}
                  maxWidth="85%"
                  text={
                    is24meApp()
                      ? i18n.t('tfmTitle')
                      : i18n.t('calendarPlatformBuilt')
                  }
                />
                <SubTitleComponent
                  text={
                    is24meApp() ? i18n.t('tfmDesc') : i18n.t('groupcalSubTitle')
                  }
                />
              </Box>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

interface CodeInputBlockProps {
  phone: string
  onAuth: (b: boolean) => void
  showCode: (b: boolean) => void
  onGenerateCaptcha: (b: boolean) => void
  countryCode: string
  executeRecaptcha: () => Promise<string | null>
}

let pinInput: PinInput | null = null

function CodeInputBlock(props: CodeInputBlockProps) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const auth = useAuth()
  const [loading, setLoading] = useState<number>(0)
  const [errorText, setErrorText] = useState<string | undefined>(' ')
  const connection = useConnection()
  const tabActive = useTabActive()
  const calendar = useCalendar()
  const [showHelp, setShowHelp] = useState(false)

  const tfmTour = useTfmTourProvider()
  const tour = useTour()

  // Pin input size: 45px between 900px-1000px, 50px otherwise
  const isMidScreen = useMediaQuery(
    '(min-width: 990px) and (max-width: 1100px)'
  )

  const checkSMSCode = async (pin: string) => {
    setLoading(1)
    setErrorText(' ')
    if (!navigator.onLine) {
      connection.setNoInternetVisible(true)
    } else {
      try {
        let token
        let userPhone

        if (auth.codeProvider === CodeProvider.Firebase) {
          const [firebaseToken, phone] = await verifyPinCode(pin)
          userPhone = phone
          token = firebaseToken
        } else if (auth.codeProvider === CodeProvider.Checkmobi) {
          const data: CheckmobiCheckPinRequest = {
            id: LocalStorage.get(LocalStorageKey.CHECKMOBI_TOKEN) ?? '',
            pin: pin
          }
          await validatePin(data).then((response) => {
            if (!response.validated) throw new Error('Invalid pin')
            userPhone = `${response.number}`
            token = LocalStorage.get(LocalStorageKey.CHECKMOBI_TOKEN)
          })
        }

        logPhoneAnalytics(
          EventCategory.CODE_VERIF,
          Events.PHONE_VERIF_CORRECT,
          auth.phoneType,
          props.countryCode,
          props.phone
        )

        //after successful code validation, we reset lastupdate
        LocalStorage.remove(LocalStorageKey.LAST_UPDATE)

        if (userPhone && token) {
          if (auth.phoneTypeUpdateFlow) {
            // we need to update account phone type

            Account().then((account) => {
              updateAccountOnServer({
                ...(Database.toServerObject(account) as AccountDoc),
                PhoneNumberType: DocPhoneNumberType.MOBILE,
                PhoneNumber:
                  LocalStorage.get(LocalStorageKey.PHONE_NUMBER) ?? '',
                LastUpdate: dayjs().unix().toString()
              })
                .then(() => {
                  Database.write(async () => {
                    account.update((acc) => {
                      acc.PhoneNumberType = DocPhoneNumberType.MOBILE
                      acc.PhoneNumber =
                        LocalStorage.get(LocalStorageKey.PHONE_NUMBER) ?? ''
                    })
                  })
                })
                .then(() => {
                  LocalStorage.set(
                    LocalStorageKey.PHONE_NUMBER_TYPE,
                    DocPhoneNumberType.MOBILE
                  )
                  auth.onPhoneTypeUpdateFlow(false)

                  if (tour.setSteps && tfmTour.getStepsByMode) {
                    const steps = tfmTour.getStepsByMode(
                      ONBOARDING_MODE.FIRST_LOGIN
                    )
                    tour.setSteps(steps)
                    tour.setCurrentStep(0)
                    if (steps.length > 0) {
                      tour.setIsOpen(true)
                    }
                  }
                })
                .catch((e) => {
                  Account().then((acc) => {
                    LocalStorage.set(
                      LocalStorageKey.PHONE_NUMBER,
                      acc.PhoneNumber
                    )
                  })

                  if (e instanceof AxiosError) {
                    if (e.response?.status === 409) {
                      setErrorText(t('alreadyUsedPhoneNumber'))
                    }
                  } else {
                    setErrorText(t('oopsError'))
                  }
                })
            })
            return
          }
          try {
            await signUp(
              userPhone,
              token,
              undefined,
              undefined,
              undefined,
              auth.phoneType === PhoneType.Landline
                ? DocPhoneNumberType.LANDLINE
                : DocPhoneNumberType.MOBILE
            ).then(() => {
              props.onAuth(true)
              Analytics.getInstance().sendEvent(Events.LOGIN_SUCCESS)

              LocalStorage.set(
                LocalStorageKey.PHONE_NUMBER_TYPE,
                auth.phoneType === PhoneType.Landline
                  ? DocPhoneNumberType.LANDLINE
                  : DocPhoneNumberType.MOBILE
              )
              calendar.onView(CalendarRange.WEEK)
            })
          } catch (e) {
            Analytics.getInstance().sendEvent(Events.LOGIN_ERROR)
            Logger.red('error during signup-login', e)
            setErrorText(`${t('oopsError')}`)
            setLoading(0)
          }
        }
      } catch (e) {
        let errorText = t('smsCodeWrong')

        if (e instanceof Error && e.message === 'Invalid pin') {
          // checkmobi error
          logPhoneAnalytics(
            EventCategory.CODE_VERIF,
            Events.PHONE_VERIF_INCORRECT,
            auth.phoneType,
            props.countryCode,
            props.phone
          )
        } else if (e instanceof FirebaseError) {
          console.log(`code ${e.code}; msg ${e.message}`)

          if (e.code === 'auth/invalid-verification-code') {
            logPhoneAnalytics(
              EventCategory.CODE_VERIF,
              Events.PHONE_VERIF_INCORRECT,
              auth.phoneType,
              props.countryCode,
              props.phone
            )
          }
        }
        setErrorText(errorText)

        pinInput?.clear()
        pinInput?.focus()
      }
    }

    setLoading(0)
  }

  const onCodeEntered = async (value: string) => {
    await checkSMSCode(value)
  }

  const onCodeChange = () => {
    setErrorText(' ')
  }

  const pinContainerRef = useRef<HTMLDivElement>(null)

  const getPinInputs = () => {
    if (!pinContainerRef.current) return [] as HTMLInputElement[]
    return Array.from(
      pinContainerRef.current.querySelectorAll<HTMLInputElement>('input')
    )
  }

  const clearPinSelection = (inputs: HTMLInputElement[]) => {
    inputs.forEach((input) => {
      const caret = input.value.length
      input.setSelectionRange(caret, caret)
    })
  }

  const blurPinInputs = () => {
    getPinInputs().forEach((input) => input.blur())
  }

  /**
   * Smart focus:
   * - No digits entered → focus first box
   * - Some digits entered → focus first empty box
   * - All filled → focus last box (e.g. user came back to review)
   */
  const smartFocusPin = () => {
    const inputs = getPinInputs()
    if (inputs.length > 0) {
      const firstEmpty = inputs.find((input) => !input.value)
      const target = firstEmpty ?? inputs[inputs.length - 1]
      target.focus()
      clearPinSelection(inputs)
    } else {
      pinInput?.focus()
    }
  }

  useEffect(() => {
    if (tabActive) smartFocusPin()
  }, [tabActive])

  const onGoBack = () => {
    props.showCode(false)
    props.onGenerateCaptcha(false)
  }

  const parsedNum = parsePhoneNumber(`+${props.phone}`)
  const resendTitle =
    auth.phoneType === PhoneType.Landline ? t('recall') : t('resend')

  useEffect(() => {
    setShowHelp(false)
  }, [auth.isAuth])

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <TitleComponent
        color={theme.palette.mode === 'dark' ? 'white' : 'black'}
        text={`${t('getStarted')}`}
      />
      <BlockContainer
        container
        loading={loading}
        onClick={(e: React.MouseEvent) => {
          // Let clicks inside the verify-pin container keep their native target.
          if (
            e.target instanceof Node &&
            pinContainerRef.current?.contains(e.target)
          ) {
            e.stopPropagation()
          } else {
            smartFocusPin()
          }
        }}
      >
        <Grid
          container
          flexDirection={'row'}
          justifyContent={'center'}
          columnSpacing={2}
        >
          <Grid item>
            <MiscText
              fontWeight={400}
              color={'var(--color-text-secondary)'}
              fontSize={'16px'}
              lineHeight={'22px'}
            >
              &#x202D;
              {`${parsedNum?.formatInternational()}`}
            </MiscText>
          </Grid>
          <Grid item>
            <MiscText
              sx={{ cursor: 'pointer' }}
              color={theme.palette.button.text.groupcal}
              onClick={onGoBack}
              className="edit-phone"
            >
              {`(${t('edit').toLowerCase()})`}
            </MiscText>
          </Grid>
        </Grid>
        <MiscText
          width={'100%'}
          textAlign={'center'}
          fontSize={'16px'}
          lineHeight={'22px'}
          fontWeight={500}
          color={'var(--color-text-primary)'}
          mt={4}
          mb={6}
        >
          {t('enterCode')}
        </MiscText>
        <div ref={pinContainerRef} className="verify-pin-container">
          <PinInput
            length={6}
            ref={(n) => (pinInput = n)}
            initialValue=""
            onComplete={onCodeEntered}
            onChange={onCodeChange}
            type="numeric"
            inputMode="number"
            autoSelect={false}
            style={{
              display: 'flex',
              direction: 'ltr',
              alignContent: 'center',
              justifyContent: 'center'
            }}
            inputStyle={{
              fontSize: 18,
              borderColor: theme.palette.border.primary,
              borderWidth: '2px',
              borderStyle: 'solid',
              padding: '1px',
              borderRadius: 12,
              width: isMidScreen ? 45 : 50,
              height: isMidScreen ? 45 : 50,
              backgroundColor: 'var(--color-surface-card)',
              color: theme.palette.text.primary
            }}
            inputFocusStyle={{
              borderColor: APP_COLOR,
              borderWidth: '2px',
              borderStyle: 'solid'
            }}
            regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
          />
        </div>
      </BlockContainer>
      <Grid
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        width={'100%'}
      >
        {errorText != ' ' ? (
          <MiscText color={'red'} fontWeight={400}>
            {errorText}
          </MiscText>
        ) : (
          <span>&nbsp;</span>
        )}
        <LoadingSpinner
          opacity={loading ? 1 : 0}
          loading={loading}
          sx={{ position: 'inherit' }}
        />
        <CountdownButton
          buildTitle={(seconds) => {
            return seconds > 0
              ? t('resendInType', {
                  redo: resendTitle,
                  inMinutes: dayjs(seconds * 1000).format('mm:ss')
                })
              : resendTitle
          }}
          onClick={() => {
            // after 5 seconds, set showHelp to true
            setTimeout(() => {
              if (!auth.isAuth) setShowHelp(true)
            }, 5000)

            auth.onCodeProvider(CodeProvider.Checkmobi)

            props
              .executeRecaptcha()
              .then((token) => {
                try {
                  Logger.blue('recaptcha token', token)
                  setLoading(1)
                  const checkmobiRequest: CheckmobiValidationRequest = {
                    number: `+${props.phone}`,
                    type: 'sms',
                    platform: 'web',
                    recaptchaToken: token ?? ''
                  }
                  return requestSMSFromCheckmobi(checkmobiRequest).then(
                    (data) => {
                      setLoading(0)
                      LocalStorage.set(
                        LocalStorageKey.PHONE_NUMBER,
                        data.validation_info.e164_format
                      )
                    }
                  )
                } catch (e) {
                  console.error(e)
                  return Promise.reject(e)
                }
              })
              .catch((e) => {
                console.error(e)
                setErrorText(`${t('oopsError')}`)
                setLoading(0)
              })
          }}
        />
        <Box
          onClick={() => {
            sendEmailToUs()
          }}
        >
          <Typography
            sx={{
              opacity: showHelp ? 1 : 0,
              fontSize: '13px',
              padding: 2,
              fontFamily: 'NotoSans',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {t('troubleSupport')}
          </Typography>
        </Box>
      </Grid>
    </Box>
  )
}

interface PhoneInputBlockProps {
  phone: string
  setPhone: (phone: string) => void
  onShowCode: (b: boolean) => void
  onGenerateCaptcha: (b: boolean) => void
  countryCode: string
  setCountryCode: (s: string) => void
  onAuth: (b: boolean) => void
  executeRecaptcha: () => Promise<string | null>
}

function PhoneInputBlock(props: PhoneInputBlockProps) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const tour = useTour()
  const tabActive = useTabActive()
  const [submitEnabled, setIsValidPhoneNum] = useState(
    isValidPhoneNumber(`+${props.phone}`)
  )
  const [errorText, setErrorText] = useState<string | undefined>(' ')
  const [loading, setLoading] = useState<number>(0)
  const [sureDialog, setSureDialog] = useState(false)
  const connection = useConnection()
  const requestSMSCode = () => {
    if (!navigator.onLine) connection.setNoInternetVisible(true)
    else if (submitEnabled) setSureDialog(true)
  }

  const checkPhoneValidity = (
    inputNumber: string,
    country: object,
    countries: object[]
  ) => {
    return countries.some((country) => {
      return (
        startsWith(inputNumber, (country as CountryData).dialCode) ||
        startsWith((country as CountryData).dialCode, inputNumber)
      )
    })
  }

  const [showLogin, setShowLogin] = useState<boolean>(false)
  const auth = useAuth()
  const [name, onName] = useState<string>('')
  const [email, onEmail] = useState<string>('')
  const [password, onPassword] = useState<string>('')
  const [uiState, onUIState] = useState<UISTate>(UISTate.REGULAR)

  const tfmFieldsFilled =
    uiState === UISTate.DONE_FORGOT_PASSWORD ||
    ((name || showLogin) &&
      email &&
      isValidEmail(email) &&
      (password || uiState === UISTate.FORGOT_PASSWORD))

  const onPhoneNumberChange = (value: string, data: CountryData) => {
    const withPlus = `+${value}`
    props.setCountryCode(data.dialCode)
    props.setPhone(value)
    setErrorText(' ')
    setIsValidPhoneNum(isValidPhoneNumber(withPlus))

    // Move cursor to end after state update

    setTimeout(() => {
      if (phoneInputRef.current) {
        const input = phoneInputRef.current
        if (input.value.length <= 2)
          input.setSelectionRange(input.value.length, input.value.length)
      }
    }, 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const input = e.target as HTMLInputElement
      const cursorPosition = input.selectionStart || 0
      const value = input.value

      // Check if cursor is right after a formatting character
      const formatChars = ['(', ')', ' ', '-']
      const charBeforeCursor = value[cursorPosition - 1]

      if (formatChars.includes(charBeforeCursor)) {
        e.preventDefault()

        // Find the last digit before formatting characters
        let newPosition = cursorPosition - 1
        while (
          newPosition > 0 &&
          formatChars.includes(value[newPosition - 1])
        ) {
          newPosition--
        }

        // Remove the digit and let the library reformat
        const newValue =
          value.slice(0, newPosition - 1) + value.slice(cursorPosition)
        const cleanValue = newValue.replace(/\D/g, '') // Remove all non-digits

        // Update the phone value
        const countryData =
          (window as any).intlTelInputGlobals?.getCountryData?.() || {}
        onPhoneNumberChange(cleanValue, countryData)
      }
    }
  }

  async function checkMobiSMS() {
    return props.executeRecaptcha().then((token) => {
      try {
        Logger.blue('recaptcha token', token)
        setLoading(1)
        const checkmobiRequest: CheckmobiValidationRequest = {
          number: `+${props.phone}`,
          type: 'sms',
          platform: 'web',
          recaptchaToken: token ?? ''
        }
        return requestSMSFromCheckmobi(checkmobiRequest).then((data) => {
          setLoading(0)
          LocalStorage.set(
            LocalStorageKey.PHONE_NUMBER,
            data.validation_info.e164_format
          )
          props.onShowCode(true)
        })
      } catch (e) {
        console.error(e)
        return Promise.reject(e)
      }
    })
  }

  async function processPhoneNumber() {
    generateRecaptcha()
    try {
      logPhoneAnalytics(
        EventCategory.PHONE_NUMBER,
        Events.PHONE_VERIF_REQUEST_SENT,
        auth.phoneType,
        props.countryCode,
        props.phone
      )

      if (auth.phoneType === PhoneType.Landline) {
        auth.onCodeProvider(CodeProvider.Checkmobi)
        const token = await props.executeRecaptcha()
        const checkmobiRequest: CheckmobiValidationRequest = {
          number: `+${props.phone}`,
          type: 'ivr',
          platform: 'web',
          recaptchaToken: token ?? ''
        }
        await requestSMSFromCheckmobi(checkmobiRequest).then((response) => {
          LocalStorage.set(
            LocalStorageKey.PHONE_NUMBER,
            response.validation_info.e164_format
          )
          setLoading(0)
        })
      } else {
        setTimeout(() => {
          setLoading(0)
        }, 10000)

        await (
          LocalStorage.get(LocalStorageKey.CODE_PROVIDER) ===
          CodeProvider.Checkmobi
            ? checkMobiSMS()
            : verifyPhoneNumber(`+${props.phone}`)
        )
          .then(() => {
            setLoading(0)
          })
          .catch((e) => {
            console.error(e)
            if (e instanceof AxiosError) {
              if (e.response?.status === 400) {
                if (e.response?.data?.code === 14) {
                  setErrorText(`${t('yourPhoneNumBlocked')}`)
                } else if (e.response?.data?.code === 13) {
                  // fallback to firebase

                  auth.onCodeProvider(CodeProvider.Firebase)
                  goToPinVerify()
                }
              } else {
                const errorText = t('oopsError')
                setErrorText(errorText)
              }
            } else {
              const errorText = t('oopsError')
              setErrorText(errorText)
            }
            LocalStorage.remove(LocalStorageKey.PHONE_NUMBER)

            setLoading(0)
          })
      }
    } catch (e) {
      props.onGenerateCaptcha(false)
      console.error(e)
      LocalStorage.remove(LocalStorageKey.PHONE_NUMBER)
      const errorText = t('oopsError')
      setLoading(0)
      setErrorText(errorText)
      Analytics.getInstance().sendEvent(Events.PHONE_VERIF_REQ_ERROR_FIREBASE, {
        [EventProperties.CATEGORY]: EventCategory.PHONE_NUMBER,
        [EventProperties.PHONE_NUMBER]: props.phone,
        [EventProperties.ERROR]: e
      })
    }
    if (LocalStorage.get(LocalStorageKey.PHONE_NUMBER)) {
      props.onGenerateCaptcha(false)
      props.onShowCode(true)
      logPhoneAnalytics(
        EventCategory.PHONE_NUMBER,
        Events.PHONE_VERIF_REQ_DONE,
        auth.phoneType,
        props.countryCode,
        props.phone
      )
      logPhoneAnalytics(
        EventCategory.CODE_VERIF,
        Events.PAGE_LOAD_VERIF,
        auth.phoneType,
        props.countryCode,
        props.phone
      )
    }
  }

  const goToPinVerify = () => {
    setSureDialog(false)
    props.onGenerateCaptcha(true)
    setLoading(1)
    setErrorText(' ')
    //in order to sync captcha, because we do not use unmounting in login process, we need a delay
    setTimeout(() => {
      processPhoneNumber()
    }, 500)
  }

  const closeSureDialog = () => {
    setSureDialog(false)
  }

  const phoneInputRef = useRef<HTMLInputElement>()

  useEffect(() => {
    if (tabActive) phoneInputRef?.current?.focus()
  }, [tabActive])

  useEffect(() => {
    const keyEnter = async (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (submitEnabled || tfmFieldsFilled)) {
        if (isGroupcalApp()) {
          if (!sureDialog) {
            event.preventDefault()
            requestSMSCode()
          } else {
            closeSureDialog()
            goToPinVerify()
          }
        } else {
          onTFMLogin()
        }
      }
    }
    document.addEventListener('keydown', keyEnter)

    return () => {
      document.removeEventListener('keydown', keyEnter)
    }
  }, [submitEnabled, sureDialog, tfmFieldsFilled, uiState])

  const parsedNum = parsePhoneNumber(`+${props.phone}`)

  const calendar = useCalendar()

  const onTFMLogin = async () => {
    setLoading(1)
    if (uiState === UISTate.REGULAR) {
      try {
        if (showLogin) {
          try {
            await cacheAndLogin(email, password, true).then((response) => {
              console.log(response)

              if (response instanceof AxiosError) {
                setErrorText(`${t('loginFailed')}`)
              } else {
                if (response) {
                  props.onAuth(true)
                  Analytics.getInstance().sendEvent(Events.LOGIN_SUCCESS)
                  calendar.onView(CalendarRange.WEEK)
                } else {
                  setErrorText(GENERIC_ERROR)
                }
              }

              setLoading(0)
            })
          } catch (e) {
            setErrorText(`${t('loginFailed')}`)
          }
        } else {
          try {
            // Check if name has multiple components, such as "Dmytro Vodnik" or "Eric von Harlaand"
            const nameArr = name.split(' ')
            let username, lastName

            if (nameArr.length === 1) {
              // Only one word
              username = name
              lastName = 'null'
            } else if (nameArr.length === 2) {
              // Two words
              username = nameArr[0]
              lastName = nameArr[1]
            } else {
              // Three or more words
              username = nameArr[0]
              lastName = nameArr.slice(1).join(' ')
            }

            const fullname = name

            await signUp(undefined, undefined, email, password, {
              FirstName: username,
              FullName: fullname,
              LastName: lastName
            }).then((response) => {
              Logger.blue('TFM login', response)
              if (response instanceof AxiosError) {
                if (
                  response.response &&
                  response.response?.data['errorCode'] === -300
                ) {
                  setErrorText(`${t('signupFailed')}`)
                } else {
                  setErrorText(GENERIC_ERROR)
                }
              } else if (response) {
                props.onAuth(true)
                Analytics.getInstance().sendEvent(Events.LOGIN_SUCCESS)
                calendar.onView(CalendarRange.WEEK)
              } else {
                setErrorText(GENERIC_ERROR)
              }

              setLoading(0)
            })
          } catch (e) {
            setErrorText(GENERIC_ERROR)
          }
        }
      } catch (e) {
        setLoading(0)
        setErrorText(GENERIC_ERROR)
      }
    } else if (uiState === UISTate.FORGOT_PASSWORD) {
      forgotPassword(email).then(() => {
        setTimeout(() => {
          onUIState(UISTate.DONE_FORGOT_PASSWORD)
          setLoading(0)
        }, 1500)
      })
    } else if (uiState === UISTate.DONE_FORGOT_PASSWORD) {
      onUIState(UISTate.REGULAR)
      setLoading(0)
    }
  }

  let miscText = is24meApp()
    ? showLogin
      ? t('loginToYourAccount')
      : t('enterYourName')
    : auth.phoneType === PhoneType.Landline
      ? t('landlineDesc')
      : t('mobileDesc')

  if (uiState === UISTate.FORGOT_PASSWORD) {
    miscText = t('forgotPasswordDesc')
  } else if (uiState === UISTate.DONE_FORGOT_PASSWORD) {
    miscText = t('doneForgotPasswordDesc')
  }

  const is24me = is24meApp()
  const buttonMainColor = theme.palette.button.action.main
  const buttonHoverColor = theme.palette.button.action.hover
  const buttonActiveColor = theme.palette.button.action.active
  const buttonDisabledColor = theme.palette.button.action.disabled
  const buttonTextColor = theme.palette.button.action.contrastText
  const isButtonDisabled = is24me ? !tfmFieldsFilled : !submitEnabled

  const [country, setCountry] = useState<string | number | undefined>(
    userLang()
  )

  // effect to load country
  useEffect(() => {
    if (isCypress()) {
      return
    }

    // call to 3rd part service to determine country by ip https://ipapi.co/8.8.8.8/json/

    // Get API token from environment variable
    const apiToken = process.env.REACT_APP_IPAPI_TOKEN

    // Build URL with token if available
    const apiUrl = apiToken
      ? `https://ipapi.co/json/?key=${apiToken}`
      : 'https://ipapi.co/json/'

    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.country_code) {
          setCountry(data.country_code.toLowerCase())
        } else {
          setCountry(userLang())
        }
      })
      .catch((error) => {
        Logger.red('Error fetching country by IP', error)
        setCountry(userLang())
      })
  }, [])

  return (
    <BlockContainer
      container
      loading={0}
      gap={is24meApp() ? 4 : 8}
      alignItems={'center'}
      alignContent={'center'}
      justifyItems={'center'}
    >
      <Box gap={4} display={'flex'} flexDirection={'column'} width={'100%'}>
        <TitleComponent
          color={theme.palette.mode === 'dark' ? 'white' : 'black'}
          text={`${t('getStarted')}`}
        />
        <SubTitleComponent
          maxWidth="100%"
          textAlign="center"
          text={miscText}
          color={'var(--color-text-secondary)!important'}
        />
      </Box>
      {is24meApp() ? (
        <TwentyfourLoginInputs
          showLogin={showLogin}
          setShowLogin={setShowLogin}
          name={name}
          password={password}
          email={email}
          onName={onName}
          onEmail={onEmail}
          onPassword={onPassword}
          onTFMLogin={onTFMLogin}
          setErrorText={setErrorText}
          onUIState={onUIState}
          uiState={uiState}
        />
      ) : (
        <Box
          display={'flex'}
          flexDirection={'row'}
          sx={{
            direction: 'ltr',
            position: 'relative',
            zIndex: 3,
            width: '100%',
            height: '48px'
          }}
        >
          <PhoneTypePicker />
          <PhoneInput
            enableSearch
            onKeyDown={(e) => handleKeyDown(e)}
            enableTerritories
            placeholder={String(t('phoneNumberPlaceholder'))}
            inputProps={{ ref: phoneInputRef }}
            inputStyle={{
              direction: 'ltr',
              border: `1px solid ${theme.palette.border.primary}`,
              width: '250px',
              color: theme.palette.text.primary,
              backgroundColor: 'var(--color-surface-card)',

              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: '25px',
              borderBottomRightRadius: '25px',
              height: '48px'
            }}
            buttonStyle={{
              borderTopLeftRadius: '25px',
              borderBottomLeftRadius: '25px',
              border: `1px solid ${theme.palette.border.primary}`,
              borderRight: `1px solid ${theme.palette.border.primary}`,
              backgroundColor: 'var(--color-surface-subtle)'
            }}
            buttonClass="flag-dropwdown"
            country={country}
            masks={{
              ua: '.. ... .. ..',
              bg: '.. ... ....',
              il: '.. ... ....'
            }}
            value={props.phone}
            onChange={onPhoneNumberChange}
            isValid={checkPhoneValidity}
          />
        </Box>
      )}
      <Grid
        justifyContent={'center'}
        width={'100%'}
        display={'flex'}
        alignItems={'center'}
        flexDirection={'column'}
        gap={'0.2rem'}
      >
        {errorText != ' ' ? (
          <MiscText fontWeight={400} color={'red'}>
            {errorText}
          </MiscText>
        ) : (
          <MiscText fontWeight={400} color={'red'}>
            &nbsp;
          </MiscText>
        )}
        {loading === 1 ? (
          <LoadingSpinner
            loading={1}
            sx={{
              position: 'inherit',
              height: 48,
              top: '0px !important',
              right: '0px !important'
            }}
          />
        ) : (
          <Grid
            display={'flex'}
            width={'100%'}
            justifyContent={'center'}
            alignContent={'center'}
            alignItems={'center'}
            flexDirection={'column'}
            gap={2}
          >
            <Box position={'relative'} width={'100%'}>
              <Button
                id="loginButton"
                fullWidth
                disableElevation
                onClick={is24meApp() ? onTFMLogin : requestSMSCode}
                disabled={isButtonDisabled}
                sx={{
                  color: buttonTextColor,
                  fontWeight: 500,
                  height: '48px',
                  textTransform: 'none',
                  paddingY: 1.5,
                  borderRadius: '25px',
                  backgroundColor: buttonMainColor,
                  '&:hover': {
                    backgroundColor: buttonHoverColor
                  },
                  '&:active': {
                    backgroundColor: buttonActiveColor
                  },
                  '&.Mui-disabled': {
                    backgroundColor: buttonDisabledColor,
                    color: buttonTextColor,
                    cursor: 'not-allowed'
                  }
                }}
              >
                <div
                  style={{
                    color: 'inherit',
                    fontSize: '20px'
                  }}
                >
                  {uiState === UISTate.FORGOT_PASSWORD && t('resetPassword')}
                  {uiState === UISTate.REGULAR &&
                    (is24meApp()
                      ? showLogin
                        ? t('login')
                        : t('createAccount')
                      : auth.phoneType === PhoneType.Landline
                        ? t('receiveACall')
                        : t('sendSmsButton'))}
                  {uiState === UISTate.DONE_FORGOT_PASSWORD && t('backToLogin')}
                </div>
              </Button>
              {isButtonDisabled && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                    cursor: 'not-allowed'
                  }}
                  onClick={() => {
                    setErrorText(
                      is24meApp()
                        ? 'Fill out all missing fields above'
                        : 'Enter your phone number'
                    )
                  }}
                />
              )}
            </Box>
            {auth.phoneTypeUpdateFlow && (
              <TextButton
                onClick={() => {
                  Account().then((acc) => {
                    if (isUserOutOfTierBasedOnLandline(acc)) {
                      LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '1')
                    }

                    auth.onPhoneTypeUpdateFlow(false)
                    tour.setIsOpen(false)
                  })
                }}
              >
                {t('cancelGoBack')}
              </TextButton>
            )}
          </Grid>
        )}
        {isGroupcalApp() && (
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={2}
            m={4}
            alignItems={'center'}
          >
            <ShieldCheck width={'20px'} height={'20px'} color={'#767676'} />
            <Typography fontSize={'12px'} lineHeight={'16px'} color={'#767676'}>
              {t('yourPrivacyProtected')}
            </Typography>
          </Box>
        )}
      </Grid>
      {sureDialog && (
        <ConfirmationDialog
          title={String(t('numberConfirmation'))}
          leftButtonColor={'black'}
          leftButton={String(t('edit'))}
          rightButton={String(t('yes'))}
          content={
            <div style={{ textAlign: 'center' }}>
              &#x202D;
              {`${parsedNum?.formatInternational()}`}
              <br />
              {`${t('phoneNumberCorrect')}`}
            </div>
          }
          handleLeftButton={closeSureDialog}
          open={sureDialog}
          handleRightButton={goToPinVerify}
          handleClose={closeSureDialog}
        />
      )}
      {auth.showLandlineDesc && (
        <ConfirmationDialog
          handleClose={() => {
            auth.setShowLandlineDesc(false)
          }}
          handleLeftButton={() => {
            auth.onPhoneType(PhoneType.Mobile)
            auth.setShowLandlineDesc(false)
          }}
          handleRightButton={() => {
            auth.setShowLandlineDesc(false)
            phoneInputRef?.current?.focus()
          }}
          title={t('signingWithLandline')}
          content={t('signingWithLandlineDesc')}
          open={auth.showLandlineDesc}
          rightButton={`${t('continue')}`}
          leftButtonColor="black"
          rightButtonColor={APP_COLOR}
        />
      )}
    </BlockContainer>
  )
}

interface GetStartedTitleProps {
  text: string
  textAlign?: 'left' | 'center' | 'right'
  maxWidth?: string
  color?: string
}

function SubTitleComponent({
  text,
  color,
  maxWidth,
  textAlign
}: {
  text: string
  color?: string
  maxWidth?: string
  textAlign?: 'left' | 'center' | 'right'
}) {
  return (
    <Typography
      textAlign={textAlign ?? (i18n.dir() === 'ltr' ? 'left' : 'right')}
      sx={{
        color: color ?? 'var(--color-text-secondary)',
        fontWeight: 400,
        maxWidth: maxWidth ?? '85%',

        '@media (min-width: 1500px)': {
          fontSize: '18px',
          lineHeight: '24px'
        },
        '@media (min-width: 1200px) and (max-width: 1499px)': {
          fontSize: '16px',
          lineHeight: '24px'
        },
        '@media (min-width: 990px) and (max-width: 1199px), (max-height: 900px)':
          {
            fontSize: '16px',
            lineHeight: '24px'
          }
      }}
    >
      {text}
    </Typography>
  )
}

function TitleComponent({
  text,
  textAlign = 'center',
  maxWidth = '100%',
  color = undefined
}: GetStartedTitleProps) {
  const theme = useTheme()
  return (
    <Typography
      fontWeight={500}
      textAlign={textAlign}
      maxWidth={maxWidth ?? '100%'}
      color={theme.palette.text.primary}
      sx={{
        '@media (min-width: 1500px)': {
          fontSize: '40px'
        },
        '@media (min-width: 1200px) and (max-width: 1499px)': {
          fontSize: '36px'
        },
        '@media (min-width: 990px) and (max-width: 1199px), (max-height: 900px)':
          {
            fontSize: '32px'
          }
      }}
      lineHeight={'44px'}
    >
      {text}
    </Typography>
  )
}

/**
 * In order to show correct num we need to remove zero, because we show it on country code
 * @param num phone number to remove zero from
 * @returns num without phone number
 */
export function removeZeroIfNeeded(num: string | null | undefined) {
  if (!num) return ''
  if (num.startsWith('0')) return num.substring(1, num.length)
  return num
}

function logPhoneAnalytics(
  categoty: string,
  eventName: Events,
  type: PhoneType,
  countryCode: string,
  phoneNumber: string
) {
  Analytics.getInstance().sendEvent(eventName, {
    [EventProperties.CATEGORY]: categoty,
    [EventProperties.SELECTED_PHONE_TYPE]: type,
    [EventProperties.COUNTRY_CODE]: countryCode,
    [EventProperties.PHONE_NUMBER]: phoneNumber
  })
}
